import { ColorStyle, getColorComponentById } from "components/select/lineUtility";
import { SPECIAL_STYLE_CATEGORIES, SPECIAL_STYLE_CATEGORIES_TYPE } from "config/config-general";
import { Feature } from "geojson";
import { LegendElement } from "lib/leaflet/print";
import { IconID, PredefinedPatternTypes, CustomCategories } from "types/auth";
import { FeatureStyleObject, FirestoreLayerLegend } from "types/firestoreFeatures";

// Project import
import { isStackInventoryType } from "./inventories";
import { MapModes } from "types/mapData";
import { categoriesFilterCheck } from "utils/categories";
import { addObjectIfNotExists, getCommonElements } from "utils/dataManipulation";

interface GetPrintLegendsProps {
  features?: Feature[];
  /** Stack markers already filtered by the rendered print map. */
  renderedStackFeatures?: Feature[];
  transparent?: boolean;
  mode?: MapModes;
  activeCategories: string[] | null;
  categoriesFilterLogicType: "AND" | "OR";
  customCategories?: CustomCategories;
  filterIds: string[] | null;
  externalLayersLegend: { title: string; layerId?: string; legends: FirestoreLayerLegend[] }[];
  getSpeciesColor: (species: string) => string;
  getSpeciesNameAndAbbreviation: (species: string) => { name: string; abbreviation: string };
  getLineStyle: (
    name?: string,
    styleObject?: FeatureStyleObject,
  ) => { color: string; dashArray: string | null; weight: number };
  translate: (key: string) => string;
}

export function getPrintLegends({
  features,
  renderedStackFeatures,
  transparent,
  mode,
  activeCategories,
  categoriesFilterLogicType,
  customCategories,
  filterIds,
  externalLayersLegend,
  getSpeciesColor,
  getSpeciesNameAndAbbreviation,
  getLineStyle,
  translate,
}: GetPrintLegendsProps): LegendElement[] {
  let polygonLegends: LegendElement[] = [];
  let polygonNotProgramDefinedLegends: LegendElement[] = [];
  let lineLegends: LegendElement[] = [];
  let lineLegendsNotProgramDefined: LegendElement[] = [];
  let pointLegends: LegendElement[] = [];
  let layerLegends: LegendElement[] = [];
  const stackLegends = new Map<string, LegendElement>();
  const addStackLegend = (feature: Feature) => {
    const inventoryType = feature.properties?.inventoryType;
    if (!isStackInventoryType(inventoryType) || stackLegends.has(inventoryType)) return;
    stackLegends.set(inventoryType, {
      type: "Point",
      color: "#505050",
      iconId: feature.properties?.styleObject?.pointIcon || "FmdGoodRoundedIcon",
      name: translate(`inventory-item-${inventoryType}`),
    });
  };
  renderedStackFeatures?.forEach(addStackLegend);

  // Set legends from features
  if (features) {
    features.forEach((feat) => {
      const { type } = feat.geometry;
      if (isStackInventoryType(feat.properties?.inventoryType)) {
        if (
          renderedStackFeatures === undefined &&
          (!activeCategories || categoriesFilterCheck({
            categories: activeCategories,
            featureType: type,
            featureCategories: [
              ...(feat.properties?.categories || []),
              translate(`tag-${feat.properties?.inventoryType}`),
            ],
            logic: categoriesFilterLogicType,
          }))
        ) {
          addStackLegend(feat);
        }
        return;
      }
      //@ts-ignore
      const { categories, mainSpecies, title, styleObject } = feat.properties;
      // We assume that if no active categories are set, all features are shown
      let legElement = null;
      if (!activeCategories) {
        legElement = getFeatureLegend({
          type: type,
          transparent: transparent !== undefined ? transparent : false,
          mode: mode !== undefined ? mode : "light",
          categories: categories ? categories : [],
          customCategories,
          styleObject: styleObject ? styleObject : undefined,
          species: mainSpecies,
          title,
          getSpeciesColor,
          getSpeciesNameAndAbbreviation,
          getLineStyle,
          translate,
        });
      } else if (activeCategories && activeCategories.length > 0 && categories) {
        if (
          categoriesFilterCheck({
            categories: activeCategories,
            featureType: type,
            featureCategories: categories,
            logic: categoriesFilterLogicType,
          })
        ) {
          legElement = getFeatureLegend({
            type: type,
            transparent: transparent !== undefined ? transparent : false,
            mode: mode !== undefined ? mode : "light",
            categories,
            customCategories,
            styleObject: styleObject ? styleObject : undefined,
            species: mainSpecies,
            title,
            getSpeciesColor,
            getSpeciesNameAndAbbreviation,
            getLineStyle,
            translate,
          });
        }
      }
      // Check for concat of legends
      if (!legElement || !legElement.legend) return;

      if (type === "Point" || type === "MultiPoint") {
        addToExistingObjectInArrayIfExists(pointLegends, legElement.legend, ["type", "iconId"]);
      } else if (type === "Polygon" || type === "MultiPolygon") {
        if (!legElement.programDefined) {
          addToExistingObjectInArrayIfExists(polygonNotProgramDefinedLegends, legElement.legend, [
            "type",
            "color",
          ]);
        } else {
          addObjectIfNotExists(polygonLegends, legElement.legend);
        }
      } else if (type === "LineString" || type === "MultiLineString") {
        if (!legElement.programDefined) {
          addToExistingObjectInArrayIfExists(lineLegendsNotProgramDefined, legElement.legend, [
            "type",
            "color",
          ]);
        } else {
          addObjectIfNotExists(lineLegends, legElement.legend);
        }
      }
    });
  }
  // Set legends from layers
  if (externalLayersLegend && externalLayersLegend.length > 0) {
    externalLayersLegend.forEach((leg) => {
      // If layerId exist insert it in each legend entry
      let newLegends: LegendElement[] = [...leg.legends];
      if (leg.layerId) {
        newLegends = leg.legends.map((l) => ({
          ...l,
          layerId: leg.layerId,
        }));
      }
      layerLegends = [...layerLegends, ...newLegends];
    });
  }

  return [
    ...polygonLegends.sort((a, b) => {
      const valueA = typeof a.name === "string" ? a.name : "";
      const valueB = typeof b.name === "string" ? b.name : "";
      return valueA.localeCompare(valueB, "da-DK");
    }),
    ...polygonNotProgramDefinedLegends.sort((a, b) => {
      const valueA = typeof a.name === "string" ? a.name : "";
      const valueB = typeof b.name === "string" ? b.name : "";
      return valueA.localeCompare(valueB, "da-DK");
    }),
    ...lineLegends.sort((a, b) => {
      const valueA = typeof a.name === "string" ? a.name : "";
      const valueB = typeof b.name === "string" ? b.name : "";
      return valueA.localeCompare(valueB, "da-DK");
    }),
    ...lineLegendsNotProgramDefined.sort((a, b) => {
      const valueA = typeof a.name === "string" ? a.name : "";
      const valueB = typeof b.name === "string" ? b.name : "";
      return valueA.localeCompare(valueB, "da-DK");
    }),
    ...pointLegends.sort((a, b) => {
      const valueA = typeof a.name === "string" ? a.name : "";
      const valueB = typeof b.name === "string" ? b.name : "";
      return valueA.localeCompare(valueB, "da-DK");
    }),
    ...Array.from(stackLegends.values()).sort((a, b) => a.name.localeCompare(b.name, "da-DK")),
    ...layerLegends,
  ];
}

interface GetFeatureLegendProps {
  type: string;
  styleObject?: FeatureStyleObject;
  transparent: boolean;
  mode: MapModes;
  species: string;
  title?: string;
  categories: string[];
  customCategories?: CustomCategories;
  getSpeciesColor: (species: string) => string;
  getSpeciesNameAndAbbreviation: (species: string) => { name: string; abbreviation: string };
  getLineStyle: (
    name?: string,
    styleObject?: FeatureStyleObject,
  ) => { color: string; dashArray: string | null; weight: number };
  translate: (key: string) => string;
}
function getFeatureLegend({
  type,
  styleObject,
  transparent,
  mode,
  categories,
  customCategories,
  species,
  title,
  getSpeciesColor,
  getSpeciesNameAndAbbreviation,
  getLineStyle,
  translate,
}: GetFeatureLegendProps): { legend: LegendElement | null; programDefined: boolean } {
  let programDefined = false;
  let legend: LegendElement = {
    name: "",
    abr: undefined,
    cat: undefined,
    color: "#505050",
    type: "UnfilledRectFilledRect",
  };
  // Check for feature type
  if (type === "Point" || type === "MultiPoint") {
    // Check style object
    if (!styleObject) return { legend: null, programDefined };
    const { pointIcon } = styleObject;
    // Check for point icon
    if (!pointIcon || pointIcon === "Text") return { legend: null, programDefined };
    legend.type = "Point";
    legend.iconId = pointIcon as IconID;
    legend.name = title ? title : "";
    return { legend, programDefined };
  }
  // Get name and abbreviation
  let lineType: "Line" | "DashesLine" | null = null;
  if (species) {
    programDefined = true;
    const { name, abbreviation } = getSpeciesNameAndAbbreviation(species);
    legend.name = name;
    legend.abr = abbreviation ? abbreviation : undefined;
    if (type === "LineString" || type === "MultiLineString") {
      lineType = "Line";
      const lineSty = getLineStyle(species);
      if (lineSty.dashArray) lineType = "DashesLine";
    }
  } else if (title) {
    legend.name = title;
  }

  // Get color (default transparent light)
  if (transparent) {
    if (mode === "dark") {
      legend.color = "#ffffff";
    }
  } else {
    if (species) {
      legend.color = getSpeciesColor(species);
    } else if (styleObject && styleObject.lineColor) {
      legend.color = getColorComponentById(styleObject.lineColor as ColorStyle);
    }
  }
  // Get category and style based on categories
  const catStyle = getLegendStyleAndCat(categories, customCategories, styleObject);
  legend.cat = undefined;
  if (catStyle.customCat) {
    legend.cat = catStyle.customCat;
    if (SPECIAL_STYLE_CATEGORIES.includes(catStyle.customCat as SPECIAL_STYLE_CATEGORIES_TYPE)) {
      legend.cat = translate("tag-" + catStyle.customCat);
    }
  } else if (catStyle.cat) {
    legend.cat = translate("tag-" + catStyle.cat);
  }
  legend.type = lineType ? lineType : catStyle.type;
  return { legend, programDefined };
}

function getLegendStyleAndCat(
  categories: string[] | null,
  customCategories?: CustomCategories,
  styleObject?: FeatureStyleObject,
): {
  type: LegendElement["type"];
  cat?: SPECIAL_STYLE_CATEGORIES_TYPE;
  customCat?: string;
} {
  const DefaultStyle = "FilledRect";
  if (!categories || categories.length === 0) {
    if (styleObject) return { type: getNotProgramDefinedLegendStyle(styleObject) };
    return { type: DefaultStyle, cat: undefined };
  }
  // Check for custom category match and return if match.
  // We check this before special style categories, as custom category styles can be applied on top of special style categories
  if (customCategories) {
    for (const cat of categories) {
      const patternType = customCategories[cat]?.style?.patternType;
      if (patternType) {
        return getCustomCategoryLegendStyle(cat, patternType);
      }
    }
  }
  // Check for special style categories
  const commonElements = getCommonElements(
    categories,
    SPECIAL_STYLE_CATEGORIES as unknown as string[],
  );
  // If no special style return standard;
  if (commonElements.length === 0) {
    if (styleObject) return { type: getNotProgramDefinedLegendStyle(styleObject) };
    return { type: DefaultStyle, cat: undefined };
  }
  // If special style category return that style
  return getSpecialLegendCatAndStyle(commonElements[0] as SPECIAL_STYLE_CATEGORIES_TYPE);
}

function getNotProgramDefinedLegendStyle(styleObject: FeatureStyleObject): LegendElement["type"] {
  const { polygonStyle, lineStyle } = styleObject;
  if (polygonStyle) {
    return polygonStyle === "Filled" ? "FilledRect" : "UnfilledRect";
  } else if (lineStyle) {
    return lineStyle === "Solid" ? "Line" : "DashesLine";
  }
  return "FilledRect";
}

function getSpecialLegendCatAndStyle(category: SPECIAL_STYLE_CATEGORIES_TYPE): {
  type: LegendElement["type"];
  cat?: SPECIAL_STYLE_CATEGORIES_TYPE;
} {
  switch (category) {
    case "intensive":
      return { type: "DotsFilledRect", cat: "intensive" };
    case "biodiversity":
      return { type: "DashesStripesFilledRect", cat: "biodiversity" };
    case "untouched":
      return { type: "StripesFilledRect", cat: "untouched" };
    default:
      return { type: "FilledRect", cat: undefined };
  }
}

function getCustomCategoryLegendStyle(
  category: string,
  patternType: PredefinedPatternTypes,
): {
  type: LegendElement["type"];
  customCat?: string;
} {
  switch (patternType) {
    case "dot":
    case "dotDense":
    case "dotSparse":
      return { type: "DotsFilledRect", customCat: category };

    case "ring":
      return { type: "RingFilledRect", customCat: category };

    case "diamond":
      return { type: "DiamondFilledRect", customCat: category };
    case "square":
      return { type: "SquareFilledRect", customCat: category };

    case "triangle":
      return { type: "TriangleFilledRect", customCat: category };

    case "cross":
      return { type: "CrossesUnfilledRect", customCat: category };

    case "x":
      return { type: "StarUnfilledRect", customCat: category };

    case "diagonalStripe":
    case "diagonalStripeDense":
      return { type: "StripesFilledRect", customCat: category };

    case "diagonalStripeDashedDense":
    case "diagonalStripeDashed":
      return { type: "DashesStripesFilledRect", customCat: category };

    case "horizontalStripe":
    case "horizontalStripeDense":
      return { type: "LinesUnfilledRect", customCat: category };

    case "verticalStripe":
    case "verticalStripeDashed":
    case "verticalStripeDense":
    case "verticalStripeDashedDense":
      return { type: "VerticalLinesUnfilledRect", customCat: category };
    default:
      return { type: "FilledRect", customCat: undefined };
  }
}

export function addToExistingObjectInArrayIfExists(
  array: LegendElement[],
  newObj: LegendElement,
  testFields: (keyof LegendElement)[],
): void {
  const existingObj = array.find((item) =>
    testFields.every((field) => item[field] === newObj[field]),
  );

  if (existingObj) {
    const existingNames = existingObj.name.split(",").map((n) => n.trim());
    if (!existingNames.includes(newObj.name)) {
      existingNames.push(newObj.name);
      existingObj.name = existingNames.join(", ");
    }
  } else {
    array.push({ ...newObj });
  }
}
